import type {VideoStartFailedReason} from '../enums/VideoStartFailedReason';

import type {AnalyticsConfig} from './AnalyticsConfig';
import type {AnalyticsEventBase, CustomDataValuesEvent, ErrorEvent} from './EventData';
import type {Sample} from './Sample';

export type HeartbeatPayload = {
  buffered?: Sample['buffered'];
  played?: Sample['played'];
  /** Flag indicating that the event was triggered by SSAI */
  triggeredBySsai?: boolean;
};

export interface StateMachineCallbacks {
  setup: (duration: number, state: string) => void;
  startup: (duration: number, state: string) => void;
  playing: (duration: number, state: string) => void;
  heartbeat: (duration: number, state: 'playing' | 'rebuffering', payload: HeartbeatPayload) => void;
  qualitychange: (duration: number, state: string) => void;
  qualitychange_pause: (duration: number, state: string) => void;
  qualitychange_rebuffering: (duration: number, state: string) => void;
  videoChange: (event: any) => void;
  audioChange: (event: any) => void;
  audiotrack_changing: () => void;
  pause: (duration: number, state: string) => void;
  /**
   * Should send a sample with `state: "seeking"` and `duration` set to the duration of the seeking.
   */
  seeking: (duration: number) => void;
  /**
   * @deprecated use {@link StateMachineCallbacks.seeking} instead. We should send the samples with `state: "seeking"`
   * and not with any custom values like "paused_seeking" / "play_seeking" / "seeked" etc.!
   */
  paused_seeking: (duration: number, state: string) => void;
  /**
   * @deprecated use {@link StateMachineCallbacks.seeking} instead. We should send the samples with `state: "seeking"`
   * and not with any custom values like "paused_seeking" / "play_seeking" / "seeked" etc.!
   */
  end_play_seeking: (duration: number, state: string) => void;
  rebuffering: (duration: number, state: string) => void;
  error: (event: Readonly<ErrorEvent>) => void;
  end: (duration: number, state: string) => void;
  unload: (duration: number, state: string) => void;
  ad: (duration: number, state: string) => void;
  mute: () => void;
  unMute: () => void;
  subtitle_changing: () => void;
  setVideoTimeEndFromEvent: (analyticsEventBase: AnalyticsEventBase) => void;
  setVideoTimeStartFromEvent: (analyticsEventBase: AnalyticsEventBase) => void;
  videoStartFailed: (duration: number, reason: VideoStartFailedReason) => void;
  startCasting: (duration: number, event: any) => void;
  casting: (duration: number, event: any) => void;
  manualSourceChange: (event: {config: AnalyticsConfig}) => void;
  initialSourceChange: (event: {config: AnalyticsConfig}) => void;
  muting_ready: (duration: number, state: string, event: any) => void;
  /**
   * Should change {@link CustomDataValues} of analytics module.
   *
   * @remarks
   *
   * It is called via the default "onleavestate" state machine callback, and because of that:
   *    - it has to match the state name "CUSTOMDATACHANGE"
   *    - each parameter has to of `undefined` type (unintentionally, it can be called at any time, from any state)
   */
  customdatachange: (
    duration: number | undefined,
    state: string | undefined,
    eventData: CustomDataValuesEvent | undefined,
  ) => void;

  /** shuts down the collection and removes/clears all timeouts, intervals, event listeners */
  release: () => void;
}

/**
 * {@link StateMachineCallbacks} object with each callback set to be empty function (doing nothing).
 *
 * Why?:
 *  We need to set it as default value in {@link InternalAdapter} for use case, when {@link InternalAdapter}
 *  is already created and listen to player events, but {@link Analytics} was not instantiated/initialized yet (that
 *  should set correct {@link StateMachineCallbacks} implementation)
 */
export const defaultStateMachineCallbacks: StateMachineCallbacks = {
  setup: () => {},
  startup: () => {},
  playing: () => {},
  heartbeat: () => {},
  qualitychange: () => {},
  qualitychange_pause: () => {},
  qualitychange_rebuffering: () => {},
  videoChange: () => {},
  audioChange: () => {},
  audiotrack_changing: () => {},
  pause: () => {},
  seeking: () => {},
  paused_seeking: () => {},
  end_play_seeking: () => {},
  rebuffering: () => {},
  error: () => {},
  end: () => {},
  unload: () => {},
  ad: () => {},
  mute: () => {},
  unMute: () => {},
  subtitle_changing: () => {},
  setVideoTimeEndFromEvent: () => {},
  setVideoTimeStartFromEvent: () => {},
  videoStartFailed: () => {},
  startCasting: () => {},
  casting: () => {},
  manualSourceChange: () => {},
  initialSourceChange: () => {},
  muting_ready: () => {},
  customdatachange: () => {},
  release: () => {},
};
